import {Recurso, UseCaseRecursoAsync} from 'lib.common/dist/CleanArch';
import {ErrExterno} from 'lib.common/dist/errorHandler';
import {ProcesarDatabasesInfoParams} from '../../utils/params/ProcesarDatabasesInfoParams';
import {
   DatabaseMetadata,
   DatabaseMetadataCuenta,
   DatabaseMetadataEscritorioItem
} from '../entities/DatabaseMetadata';
import {DatabaseMetadataEstado} from '../../utils/enums';
import {APP_PREFIX} from 'lib.common/dist/constantes';
import {cadenas, transformacion} from 'lib.common';
import {CuentaMaxLimites} from '../entities/CuentaMaxLimites';
import {differenceInCalendarDays, parseISO} from 'date-fns';

export class ProcesarDatabasesInfoUseCase extends UseCaseRecursoAsync<
   ProcesarDatabasesInfoParams,
   DatabaseMetadata[]
> {
   constructor() {
      super();
   }

   async call(input: ProcesarDatabasesInfoParams): Promise<Recurso<DatabaseMetadata[]>> {
      try {
         const {appInstances, ctaList, dbList, diasAdicionalesDespuesExpiracion, appId} = input;
         const ctaDic = ctaList.reduce<{[k: string]: CuentaMaxLimites}>((prev, curr) => {
            if (!prev[curr.cuenta]) {
               prev[curr.cuenta] = curr;
            }
            return prev;
         }, {});
         const today = new Date();

         const dicDbMetadata: {[k: string]: DatabaseMetadata} = {};
         for (const dbName of dbList) {
            dicDbMetadata[dbName] = {
               id: await transformacion.generarUuid(16),
               estado: DatabaseMetadataEstado.DESCONOCIDA,
               dbName: dbName,
               cuentas: [],
               escritorios: []
            };
         }

         for (const instance of appInstances) {
            //@ts-ignore
            const dbName = `${APP_PREFIX[appId]}_${cadenas.replaceAll(
               instance.idInstancia,
               '-',
               '_'
            )}`;

            if (!dicDbMetadata[dbName]) {
               dicDbMetadata[dbName] = {
                  id: await transformacion.generarUuid(16),
                  estado: DatabaseMetadataEstado.DESCONOCIDA,
                  cuentas: [],
                  escritorios: []
               };
            }
            const currDbMetada = dicDbMetadata[dbName];

            currDbMetada.idInstancia = instance.idInstancia;
            currDbMetada.denominacionInstancia = instance.denominacion;
            // Obtener cuentas
            if (instance.cuentasManager && instance.cuentasManager.length > 0) {
               const foundCtas: DatabaseMetadataCuenta[] = instance.cuentasManager.reduce<
                  DatabaseMetadataCuenta[]
               >((prev, curr) => {
                  const ctaInfo = ctaDic[curr.idCuenta];
                  if (!ctaInfo) {
                     return prev;
                  }

                  const parsedDate = ctaInfo.fechaExpiracion
                     ? parseISO(ctaInfo.fechaExpiracion)
                     : null;
                  if (currDbMetada.fechaExpiracion && parsedDate) {
                     currDbMetada.fechaExpiracion =
                        currDbMetada.fechaExpiracion > parsedDate
                           ? currDbMetada.fechaExpiracion
                           : parsedDate;
                  } else {
                     currDbMetada.fechaExpiracion = parsedDate;
                  }

                  prev.push({
                     idCuenta: curr.idCuenta,
                     idInstanciaCuenta: curr.idInstanciaCuenta,
                     fechaExpiracion: parsedDate,
                     email: ctaInfo.email
                  });
                  return prev;
               }, []);
               if (foundCtas.length > 0) {
                  currDbMetada.cuentas = currDbMetada.cuentas?.concat(foundCtas);
               }
            }
            // Obtener escritorios
            if (instance.escritoriosManager && instance.escritoriosManager.length > 0) {
               const foundEscritorios: DatabaseMetadataEscritorioItem[] = instance.escritoriosManager.map(
                  curr => {
                     return {
                        idEscritorio: curr.idEscritorio,
                        idItem: curr.idItem,
                        enPapelera: curr.enPapelera,
                        estado: curr.estado
                     };
                  }
               );
               if (foundEscritorios.length > 0) {
                  currDbMetada.escritorios = currDbMetada.escritorios?.concat(foundEscritorios);
               }
            }
         }

         // determinar estados
         for (const dbName in dicDbMetadata) {
            if (!Object.hasOwn(dicDbMetadata, dbName)) continue;

            const currDbMetada = dicDbMetadata[dbName];
            if (!currDbMetada.cuentas || currDbMetada.cuentas.length === 0) {
               currDbMetada.cuentas = null;
            }

            if (!currDbMetada.escritorios || currDbMetada.escritorios.length === 0) {
               currDbMetada.escritorios = null;
            }

            // DatabaseMetadataEstado.HUERFANA
            if (currDbMetada.dbName && !currDbMetada.idInstancia) {
               currDbMetada.estado = DatabaseMetadataEstado.HUERFANA;
               continue;
            }

            // DatabaseMetadataEstado.FANTASMA
            if (!currDbMetada.dbName && currDbMetada.idInstancia) {
               currDbMetada.estado = DatabaseMetadataEstado.FANTASMA;
               continue;
            }

            // DatabaseMetadataEstado.EXPIRADA
            if (currDbMetada.fechaExpiracion) {
               const offsetExpiracion =
                  differenceInCalendarDays(currDbMetada.fechaExpiracion, today) +
                  diasAdicionalesDespuesExpiracion;
               if (offsetExpiracion < 0) {
                  currDbMetada.estado = DatabaseMetadataEstado.EXPIRADA;
                  continue;
               }
            }

            currDbMetada.estado = DatabaseMetadataEstado.OK;
         }

         const result: DatabaseMetadata[] = Object.values(dicDbMetadata);
         return Recurso.fromData(result);
      } catch (error) {
         const errorCapa = new ErrExterno(error as Error);
         return Recurso.fromError<DatabaseMetadata[]>(errorCapa);
      }
   }
}
